import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { CategoryMappingService } from '../../../../core/services/category-mapping.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { CategoryMapping } from '../../../../core/models/category-mapping';
import { ToastModule } from 'primeng/toast';
import { CareerPath } from '../../../../core/models/career-path';
import { CareerPathService } from '../../../../core/services/career-path.service';
import { Practice } from '../../../../core/models/practice';
import { PracticeService } from '../../../../core/services/practice.service';
import { CategoryMappingsOptions } from '../../../../core/models/category-mappings-options';
import { CountryService } from '../../../../core/services/country.service';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { RequiredMarkDirective } from '../../../../shared/directives/required-mark.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CheckboxModule } from 'primeng/checkbox';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

@Component({
  selector: 'em-category-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    DropdownModule,
    ToastModule,
    RequiredMarkDirective,
    TranslatePipe,
    CheckboxModule,
  ],
  templateUrl: './category-form.component.html',
  styleUrl: './category-form.component.scss',
  providers: [MessageService],
})
export class CategoryFormComponent implements OnDestroy {
  form: FormGroup;
  careerPaths!: CareerPath[];
  filteredCareerPaths: CareerPath[] = [];
  practices: Practice[] = [];
  levels: number[] = [];
  isEditMode: boolean = false;
  groups: string[] = ['Management', 'Leader', 'BSA', 'Staff', 'Externo'];

  errorMessages: any = {
    level: 'layout.configurations.category.addCategory.levelIsRequired',
    careerPath: 'layout.configurations.category.addCategory.careerPathIsRequired',
    categoryShortLabel: 'layout.configurations.category.addCategory.categoryShortLabelIsRequired',
    categoryLongLabel: 'layout.configurations.category.addCategory.categoryLongLabelIsRequired',
    group1: 'layout.configurations.category.addCategory.group1IsRequired',
    group2: 'layout.configurations.category.addCategory.group2IsRequired',
    geoCountry: 'layout.configurations.category.addCategory.geoCountryIsRequired',
    division: 'layout.configurations.category.addCategory.divisionIsRequired',
    professionalCategorization: 'layout.configurations.category.addCategory.professionalCategorizationIsRequired',
    businessUnit: 'layout.configurations.category.addCategory.businessUnitIsRequired',
    talentFamily: 'layout.configurations.category.addCategory.talentFamilyIsRequired',
    elTalentSegment: 'layout.configurations.category.addCategory.elTalentSegmentIsRequired',
    jobRole: 'layout.configurations.category.addCategory.jobRoleIsRequired',
  };

  translations: any = {};

  countries: string[] = [];
  divisions: string[] = [];
  professionalCategories: string[] = [];
  businessUnits: string[] = [];
  talentFamilies: string[] = [];
  talentSegments: string[] = [];
  jobRoles: string[] = [];
  private destroy$ = new Subject<void>();
  categoryMappings: CategoryMapping;

  constructor(
    private countryService: CountryService,
    private fb: FormBuilder,
    private categoryMappingService: CategoryMappingService,
    private careerPathService: CareerPathService,
    private practiceService: PracticeService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) {
    this.categoryMappings = this.categoryMappingsInitialState();
    this.form = this.createForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.loadTranslations();
    this.getAllCountries();

    // Keep the Level control in sync with the selected Career Path: it stays
    // visible but disabled until a path is chosen, then it exposes that path's
    // levels (1..numberOfLevels).
    this.form
      .get('careerPath')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((careerPath: CareerPath | null) => this.syncLevelsWithCareerPath(careerPath));

    if (this.config.data) {
      this.isEditMode = !!this.config.data.editMode;
      this.fillFormOptions(this.config.data.formOptions);
    }

    // Load lookups FIRST, then populate the form in edit mode
    forkJoin({
      practices: this.practiceService.getPractices(),
      careerPaths: this.careerPathService.getAll(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ practices, careerPaths }) => {
        this.practices = practices;
        this.careerPaths = careerPaths;
        this.filteredCareerPaths = [...careerPaths];

        if (this.isEditMode && this.config.data?.category) {
          this.populateForm(this.config.data.category);
        }
      });
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.category.practice',
        'layout.configurations.category.selectPractice',
        'layout.configurations.category.addCategory.careerPath',
        'layout.configurations.category.addCategory.level',
        'layout.configurations.category.addCategory.shortLabel',
        'layout.configurations.category.addCategory.longLabel',
        'layout.configurations.category.addCategory.division',
        'layout.configurations.category.addCategory.group',
        'layout.configurations.category.addCategory.isSupervisor',
        'layout.configurations.category.addCategory.group1',
        'layout.configurations.category.addCategory.group2',
        'layout.configurations.category.addCategory.geoCountry',
        'layout.configurations.category.addCategory.professionalCategorization',
        'layout.configurations.category.addCategory.businessUnit',
        'layout.configurations.category.addCategory.talentFamily',
        'layout.configurations.category.addCategory.elTalentSegment',
        'layout.configurations.category.addCategory.jobRole',
        'layout.configurations.category.addCategory.save',
        'layout.configurations.category.addCategory.levelIsRequired',
        'layout.configurations.category.addCategory.careerPathIsRequired',
        'layout.configurations.category.addCategory.categoryShortLabelIsRequired',
        'layout.configurations.category.addCategory.categoryLongLabelIsRequired',
        'layout.configurations.category.addCategory.group1IsRequired',
        'layout.configurations.category.addCategory.group2IsRequired',
        'layout.configurations.category.addCategory.geoCountryIsRequired',
        'layout.configurations.category.addCategory.divisionIsRequired',
        'layout.configurations.category.addCategory.professionalCategorizationIsRequired',
        'layout.configurations.category.addCategory.businessUnitIsRequired',
        'layout.configurations.category.addCategory.talentFamilyIsRequired',
        'layout.configurations.category.addCategory.elTalentSegmentIsRequired',
        'layout.configurations.category.addCategory.jobRoleIsRequired',
        'layout.configurations.category.addCategory.errorMessage.title',
        'layout.configurations.category.addCategory.errorMessage.alreadyExists',
        'layout.configurations.category.editCategory.errorMessage.title',
        'layout.configurations.category.editCategory.errorMessage.alreadyExists',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  categoryMappingsInitialState(): CategoryMapping {
    return {
      categoryMappingsId: {
        careerPathId: 0,
        categoryShortLabel: '',
        division: '',
        level: 0,
      },
      categoryLongLabel: '',
      group1: '',
      group2: '',
      geoCountry: '',
      professionalCategorization: '',
      businessUnit: '',
      talentFamily: '',
      elTalentSegment: '',
      jobRole: '',
      careerPath: {
        practice: '',
        name: '',
        numberOfLevels: 0,
      },
      supervisor: false,
      group: '',
    };
  }

  createForm(): FormGroup {
    return this.fb.group({
      practice: [null],
      careerPath: [null, Validators.required],
      level: [{ value: null, disabled: true }, Validators.required],
      categoryShortLabel: [null, Validators.required],
      categoryLongLabel: [null, Validators.required],
      group1: [null, Validators.required],
      group2: [null, Validators.required],
      geoCountry: [null, Validators.required],
      division: [null, Validators.required],
      professionalCategorization: [null, Validators.required],
      businessUnit: [null, Validators.required],
      talentFamily: [null, Validators.required],
      elTalentSegment: [null, Validators.required],
      jobRole: [null, Validators.required],
      supervisor: [false],
      group: [null],
    });
  }

  getAllCountries() {
    this.countryService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe((countries) => (this.countries = countries.map((country) => country.name)));
  }

  /**
   * Enables the Level control only when a Career Path is selected and exposes
   * that path's levels. The control keeps its previous value otherwise, so it
   * is always reset when the path changes or is cleared.
   */
  private syncLevelsWithCareerPath(careerPath: CareerPath | null): void {
    const level = this.form.get('level');
    const numberOfLevels = careerPath?.numberOfLevels ?? 0;
    this.levels = Array.from({ length: numberOfLevels }, (_, i) => i + 1);

    if (careerPath && numberOfLevels > 0) {
      level?.enable({ emitEvent: false });
    } else {
      level?.disable({ emitEvent: false });
    }
    level?.reset(null, { emitEvent: false });
  }

  onPracticeChange(event: DropdownChangeEvent) {
    const allCareerPaths = this.careerPaths ?? [];
    if (event.value) {
      this.filteredCareerPaths = allCareerPaths.filter(cp => cp.practice === event.value.name);
    } else {
      this.filteredCareerPaths = [...allCareerPaths];
    }
    // Clearing the Career Path also hides/resets its levels (see the
    // careerPath valueChanges subscription).
    this.form.patchValue({ careerPath: null, level: null });
    this.levels = [];
  }

  populateForm(category: CategoryMapping) {
    this.categoryMappings = { ...category };

    const practice =
      this.practices.find((p) => p.name === category.careerPath?.practice) ?? null;

    const careerPath =
      this.careerPaths.find((cp) => cp.id === category.careerPath?.id) ?? category.careerPath;

    this.levels = Array.from({ length: careerPath?.numberOfLevels ?? 0 }, (_, i) => i + 1);
    this.filteredCareerPaths = this.careerPaths.filter(
      (cp) => cp.practice === category.careerPath?.practice,
    );

    this.form.patchValue({
      practice,
      careerPath,
      level: category.categoryMappingsId?.level ?? null,
      categoryShortLabel: category.categoryMappingsId?.categoryShortLabel ?? null,
      division: category.categoryMappingsId?.division ?? null,
      categoryLongLabel: category.categoryLongLabel,
      group1: category.group1,
      group2: category.group2,
      geoCountry: category.geoCountry,
      professionalCategorization: category.professionalCategorization,
      businessUnit: category.businessUnit,
      talentFamily: category.talentFamily,
      elTalentSegment: category.elTalentSegment,
      jobRole: category.jobRole,
      supervisor: category.supervisor ?? false,
      group: category.group ?? null,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateCategory();
    } else {
      this.addCategory();
    }
  }

  addCategory() {
    const {
      level,
      careerPath,
      division,
      categoryShortLabel,
      categoryLongLabel,
      group1,
      group2,
      geoCountry,
      professionalCategorization,
      businessUnit,
      talentFamily,
      elTalentSegment,
      jobRole,
      supervisor,
      group,
    } = this.form.value;

    this.categoryMappingService
      .addCategoryMapping({
        categoryMappingsId: {
          level,
          careerPathId: careerPath.id,
          division,
          categoryShortLabel,
        },
        categoryLongLabel,
        careerPath,
        group1,
        group2,
        geoCountry,
        professionalCategorization,
        businessUnit,
        talentFamily,
        elTalentSegment,
        jobRole,
        supervisor,
        group,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (category: CategoryMapping) => {
          this.ref.close(category);
        },
        error: (error) => {
          this.throwMessage(error, 'CONFLICT', this.translations['layout.configurations.category.addCategory.errorMessage.alreadyExists']);
        },
      });
  }

  setCategoryValues(): CategoryMapping {
    const updatedId = {
      ...this.categoryMappings.categoryMappingsId,
      level: this.form.controls['level'].value,
      division: this.form.controls['division'].value,
      categoryShortLabel: this.form.controls['categoryShortLabel'].value,
      careerPathId: this.form.controls['careerPath'].value.id,
    };

    const updatedCategory: CategoryMapping = {
      ...this.categoryMappings,
      categoryMappingsId: updatedId,
      categoryLongLabel: this.form.value.categoryLongLabel,
      group1: this.form.value.group1,
      group2: this.form.value.group2,
      geoCountry: this.form.value.geoCountry,
      professionalCategorization: this.form.value.professionalCategorization,
      businessUnit: this.form.value.businessUnit,
      talentFamily: this.form.value.talentFamily,
      elTalentSegment: this.form.value.elTalentSegment,
      jobRole: this.form.value.jobRole,
      careerPath: this.form.value.careerPath,
      supervisor: this.form.value.supervisor,
      group: this.form.value.group,
    };

    return updatedCategory;
  }

  updateCategory() {
    const updatedCategory = this.setCategoryValues();
    const isChanged = JSON.stringify(this.categoryMappings) !== JSON.stringify(updatedCategory);

    if (!isChanged) {
      this.ref.close({ type: 'no-change' });
      return;
    }

    this.categoryMappingService
      .updateCategoryMapping(this.categoryMappings, updatedCategory)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (category: CategoryMapping) => {
          this.ref.close({ type: 'updated', data: category });
        },
        error: (error) => {
          this.throwMessage(error, 'NOT_FOUND', this.translations['layout.configurations.category.editCategory.errorMessage.alreadyExists']);
        },
      });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);
    return control?.invalid && (control?.dirty || control?.touched);
  }

  fillFormOptions(formOptions: CategoryMappingsOptions) {
    this.divisions = [...formOptions.divisions];
    this.professionalCategories = [...formOptions.professionalCategories];
    this.businessUnits = [...formOptions.businessUnits];
    this.talentFamilies = [...formOptions.talentFamilies];
    this.talentSegments = [...formOptions.talentSegments];
    this.jobRoles = [...formOptions.jobRoles];
  }

  throwMessage(error: any, httpStatus: string, thrownMessage: string) {
    let message: string;

    error.error.httpStatus == httpStatus ? (message = error.error.message) : (message = thrownMessage);

    this.messageService.add({
      severity: 'error',
      summary: this.translations['layout.configurations.category.addCategory.errorMessage.title'],
      detail: message,
    });
  }
  onSupervisorChange(value: any) {
    const checked = value?.checked ?? value;
    this.form.get('supervisor')?.setValue(!!checked);
  }
}